import { useState, type FormEvent } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import type { SpotCheck } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { opsApi, useOpsMutation } from '@/components/ops/api'
import { ErrorState, FormError, LoadingState } from '@/components/app/states'
import { formatDateTime } from '@/components/app/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'

export const Route = createFileRoute('/ops/spot-checks')({
  loader: () => opsApi<{ items: SpotCheck[] }>('/ops/spot-checks').then((r) => r.items),
  head: () => ({ meta: [{ title: 'Spot checks · Ops' }] }),
  component: SpotChecksPage,
  pendingComponent: () => <LoadingState label="Loading spot checks" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load spot checks" />,
})

const TONE: Record<SpotCheck['status'], string> = {
  pending: 'border-warning/40 text-warning',
  ok: 'border-success/40 text-success',
  issue: 'border-destructive/40 text-destructive',
}

function SpotChecksPage() {
  const items = Route.useLoaderData()
  const [target, setTarget] = useState<{ item: SpotCheck; status: 'ok' | 'issue' } | null>(null)
  // Separate from `target` so the form stays mounted while closing and focus returns to the row button.
  const [isOpen, setIsOpen] = useState(false)
  const [finding, setFinding] = useState('')
  const [invalid, setInvalid] = useState<string | null>(null)
  const { run, pending, error, setError } = useOpsMutation()

  const sorted = [...items].sort((a, b) => Number(b.status === 'pending') - Number(a.status === 'pending') || b.createdAt - a.createdAt)
  const pendingCount = items.filter((i) => i.status === 'pending').length

  const open = (item: SpotCheck, status: 'ok' | 'issue') => {
    setFinding(item.finding ?? '')
    setInvalid(null)
    setError(null)
    setTarget({ item, status })
    setIsOpen(true)
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!target) return
    if (target.status === 'issue' && !finding.trim()) {
      setInvalid('Describe the issue so it can feed the review rubric.')
      return
    }
    const ok = await run(() =>
      api(`/ops/spot-checks/${encodeURIComponent(target.item.orderId)}`, {
        method: 'POST',
        json: { status: target.status, finding: finding.trim() || null },
      }).then(() => true),
    )
    if (ok) setIsOpen(false)
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h1 className="text-base font-semibold">
          Spot checks <span className="font-normal text-muted-foreground">({pendingCount} pending)</span>
        </h1>
        <p className="text-muted-foreground">A 5% sample of approved sites. Open the order, compare the screenshots, then record the result.</p>
      </div>
      {sorted.length === 0 ? (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>Nothing to check</EmptyTitle>
            <EmptyDescription>Approved sites are sampled into this queue automatically.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                <TableHead>Sampled</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Finding</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((i) => (
                <TableRow key={i.orderId}>
                  <TableCell className="py-1">
                    <Link to="/ops/orders/$orderId" params={{ orderId: i.orderId }} className="font-medium underline-offset-4 hover:underline">
                      {i.businessName}
                    </Link>
                    <div className="text-xs text-muted-foreground">{i.domain}</div>
                  </TableCell>
                  <TableCell className="py-1 whitespace-nowrap text-muted-foreground">{formatDateTime(i.createdAt)}</TableCell>
                  <TableCell className="py-1">
                    <Badge variant="outline" className={TONE[i.status]}>
                      {i.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-md py-1 whitespace-pre-wrap">{i.finding ?? <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="py-1 text-right whitespace-nowrap">
                    <span className="inline-flex gap-1">
                      <Button size="xs" variant="outline" onClick={() => open(i, 'ok')} aria-label={`Mark ${i.businessName} OK`}>
                        OK
                      </Button>
                      <Button size="xs" variant="destructive" onClick={() => open(i, 'issue')} aria-label={`Report an issue on ${i.businessName}`}>
                        Issue
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={(o) => !o && !pending && setIsOpen(false)}>
        <DialogContent className="sm:max-w-md">
          {target && (
            <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
              <DialogHeader>
                <DialogTitle>{target.status === 'ok' ? 'Mark as OK' : 'Report an issue'}</DialogTitle>
                <DialogDescription>
                  {target.item.businessName} ({target.item.domain})
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="spot-finding">{target.status === 'ok' ? 'Comment (optional)' : 'Finding'}</Label>
                <Textarea id="spot-finding" rows={4} value={finding} onChange={(e) => setFinding(e.target.value)} />
              </div>
              <FormError message={invalid ?? error} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={pending}>
                  Cancel
                </Button>
                <Button type="submit" variant={target.status === 'issue' ? 'destructive' : 'default'} disabled={pending}>
                  {pending ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
